import { styled } from 'styled-components';
import { Icon, Typography, tk } from '@sunwebgroup/ui-components';
import { always, identity } from 'ramda';

interface NumberStepperProps {
  value: number;
  min?: number;
  max: number;
  onValueChange?: (value: number) => void;
  onIncrementClick?: () => void;
  onDecrementClick?: () => void;
}

export function NumberStepper({
  value,
  min = 0,
  max,
  onValueChange = identity,
  onIncrementClick = always(undefined),
  onDecrementClick = always(undefined),
}: NumberStepperProps) {
  const decreaseValueHandler = () => {
    if (value <= min) return;
    onValueChange(value - 1);
    onDecrementClick();
  };

  const increaseValueHandler = () => {
    if (value + 1 > max) return;
    onValueChange(value + 1);
    onIncrementClick();
  };

  return (
    <Wrapper>
      <Button disabled={value === 0} onClick={decreaseValueHandler}>
        <Icon glyph="IconMinusSmall" size="md" />
      </Button>
      <Amount>
        <Typography $variant="body-md">{value}</Typography>
      </Amount>
      <Button disabled={value === max} onClick={increaseValueHandler} data-qa="number-stepper-increment">
        <Icon glyph="IconPlusSmall" size="md" />
      </Button>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${tk('spacing_none')};
`;

const Button = styled.button`
  color: ${tk('color_semantic_icon_on_color')};
  background-color: ${tk('color_semantic_bg_input_strong')};
  width: ${tk('spacing_xl')};
  height: ${tk('spacing_xl')};
  border: none;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${tk('radius_rounded')};
  transition: background-color 0.2s ease-in-out;

  &:hover:not(:disabled) {
    background-color: ${tk('color_semantic_bg_input_strong_hover')};
  }

  &:disabled {
    background-color: ${tk('color_semantic_bg_input_strong_disabled')};
    cursor: not-allowed;
  }
`;

const Amount = styled.div`
  display: flex;
  width: 30px;
  justify-content: center;
  margin: 0 ${tk('spacing_2xs')};
`;
